// Clotho frontend API client.
// All requests go through the Vite dev server proxy in local dev,
// and through Vercel rewrites in production.

const API_BASE = import.meta.env.VITE_CLOTHO_API_BASE ?? "";

// Shared secret for the endpoints that bill the Anthropic key or write data.
// Injected at build time so the demo stays one click for a reviewer. This is a
// deliberate tradeoff, not real auth: anyone can read it out of the bundle.
// See backend/app/security.py for what it does and does not protect against.
const DEMO_KEY = import.meta.env.VITE_CLOTHO_DEMO_KEY ?? "";

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(DEMO_KEY ? { "X-Clotho-Demo-Key": DEMO_KEY } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(
      `API ${options.method ?? "GET"} ${path} failed: ${res.status} ${body}`
    );
  }
  return res.json() as Promise<T>;
}

// ---------- Types ----------

export type SupplierMatch = {
  supplier_id: string;
  display_name: string;
  supplier_number: string | null;
  confidence: number;
  reasoning: string;
};

export type SupplierSearchResponse = {
  query: string;
  matches: SupplierMatch[];
  elapsed_ms: number;
};

export type ExtractedFields = {
  capital_project_code?: string | null;
  budget_reference?: string | null;
  cost_center?: string | null;
  description?: string | null;
  request_type?: string | null;
  value?: number | null;
  urgency?: string | null;
  supplier_name?: string | null;
  submitted_by?: string | null;
  submitted_by_email?: string | null;
  pr_number?: string | null;
  po_number?: string | null;
  sourcing_method?: string | null;
  single_source_reason?: string | null;
};

export type ExtractionResponse = {
  extracted: ExtractedFields;
  confidence: number;
  source_type: string;
};

export type ClothoRequest = {
  request_id: string;
  capital_project_code: string | null;
  budget_reference: string | null;
  cost_center: string | null;
  description: string;
  request_type: string;
  value_usd: number | null;
  urgency: string;
  supplier_id: string | null;
  supplier_name_snapshot: string | null;
  approval_status: string;
  lifecycle_state: string;
  sourcing_method: string | null;
  submitted_by: string | null;
  created_at: string | null;
};

// ---------- Calls ----------

export function searchSuppliers(q: string): Promise<SupplierSearchResponse> {
  const params = new URLSearchParams({ q });
  return request<SupplierSearchResponse>(
    `/api/suppliers/search?${params.toString()}`
  );
}

export function extractFields(
  text: string,
  sourceType = "email"
): Promise<ExtractionResponse> {
  return request<ExtractionResponse>(`/api/requests/extract`, {
    method: "POST",
    body: JSON.stringify({ text, source_type: sourceType }),
  });
}

export function createRequest(
  payload: Partial<ClothoRequest> & { description: string }
): Promise<ClothoRequest> {
  return request<ClothoRequest>(`/api/requests`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function listRequests(): Promise<{
  requests: ClothoRequest[];
  total: number;
}> {
  return request(`/api/requests`);
}
